// Small JSON-LD builders shared by pages. The site-wide Organization /
// WebSite / Person graph is defined once in src/app/layout.tsx (T-7.1); every
// page-level block here references it by @id instead of redefining it.

export const SITE_URL = 'https://ghlprime.com'
export const ORG_ID = `${SITE_URL}/#org`
export const WEBSITE_ID = `${SITE_URL}/#website`

export type Faq = { q: string; a: string }

/** HTML collapses runs of whitespace, so schema text is collapsed the same way. */
export function plainText(value: string): string {
  return String(value ?? '').replace(/\s+/g, ' ').trim()
}

/**
 * FAQPage JSON-LD. Built from the same array the page renders, so the markup
 * always matches the visible text. Entries with an empty question or answer
 * are dropped (they are never rendered either).
 */
export function faqPageJsonLd(pageUrl: string, faqs: Faq[]) {
  const mainEntity = faqs
    .map((f) => ({ q: plainText(f.q), a: plainText(f.a) }))
    .filter((f) => f.q && f.a)
    .map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    }))
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    '@id': `${pageUrl}#faq`,
    mainEntity,
  }
}

export type HowToStep = { name: string; text: string }

/** Copy that may hold [label](url) links, as schema text: "label". */
function withoutLinks(value: string): string {
  return plainText(String(value ?? '').replace(/\[([^\]]+)\]\([^)]+\)/g, '$1'))
}

/**
 * HowTo JSON-LD for a page's visible step-by-step process (the homepage's
 * "How we work", each service page's process steps). Built from the same
 * array the page renders, so the markup always matches the visible text.
 */
export function howToJsonLd(pageUrl: string, name: string, steps: HowToStep[], description?: string) {
  const step = steps
    .map((s) => ({ name: withoutLinks(s.name), text: withoutLinks(s.text) }))
    .filter((s) => s.name && s.text)
    .map((s, i) => ({ '@type': 'HowToStep', position: i + 1, name: s.name, text: s.text }))
  return {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    '@id': `${pageUrl}#howto`,
    name: withoutLinks(name),
    ...(description ? { description: withoutLinks(description) } : {}),
    step,
  }
}

export function breadcrumbJsonLd(pageUrl: string, trail: { name: string; url: string }[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    '@id': `${pageUrl}#breadcrumb`,
    itemListElement: trail.map((t, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: t.name,
      item: t.url,
    })),
  }
}

/** Serialise for a <script type="application/ld+json"> body (escapes "<"). */
export function jsonLdString(data: unknown): string {
  // '\\u003c' is the six characters \u003c (a JSON escape for "<"), so
  // text like "</script>" inside a value can't close the script tag early.
  // A single-backslash '\u003c' would be "<" itself: a no-op.
  return JSON.stringify(data).replace(/</g, '\\u003c')
}

// ---------------------------------------------------------------------------
// Site-wide entity graph (T-7.1)
// ---------------------------------------------------------------------------
// ONE @graph, emitted once from the root layout: Organization +
// ProfessionalService (#org), a Person per leader, and the WebSite. Every
// page-level schema refers to these by @id and never redefines them.
//
// All values come from lib/facts.ts. A null fact is omitted, never faked:
// foundingDate, parentOrganization, numberOfEmployees and priceRange only
// appear once the owner confirms them. There is deliberately no
// aggregateRating anywhere (self-serving review markup is ignored by Google
// and can trigger a manual action).

import { FACTS } from './facts'
import { teamMemberBySlug } from '../../content/team'

export const personId = (id: string) => `${SITE_URL}/#${id}`
export const serviceId = (path: string) => `${SITE_URL}${path}#service`

export function siteGraph() {
  const f = FACTS

  const org: Record<string, unknown> = {
    '@type': ['Organization', 'ProfessionalService'],
    '@id': ORG_ID,
    name: f.brand.name,
    legalName: f.brand.legalName,
    url: `${SITE_URL}/`,
    logo: f.brand.logo,
    image: f.brand.logo,
    description: f.brand.description,
    email: f.brand.email,
    telephone: f.brand.phone,
    address: { '@type': 'PostalAddress', ...f.address },
    areaServed: [...f.areaServed],
    knowsAbout: [
      'GoHighLevel', 'HighLevel CRM', 'A2P 10DLC', 'SaaS Mode', 'Workflow automation', 'n8n', 'Zapier',
      'Make.com', 'AI voice agents', 'Retell AI', 'Vapi', 'ElevenLabs', 'Meta Ads', 'Google Ads',
    ],
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: f.brand.phone,
      email: f.brand.email,
      contactType: 'customer support',
      availableLanguage: ['English'],
      areaServed: [...f.areaServed],
    },
    founder: f.leaders.map((l) => ({ '@id': personId(l.id) })),
    sameAs: [...f.sameAs],
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'GoHighLevel services',
      itemListElement: f.services.map((s) => ({
        '@type': 'Offer',
        itemOffered: { '@type': 'Service', '@id': serviceId(s.url) },
      })),
    },
  }

  // Only when the owner has confirmed the value (non-null in lib/facts.ts).
  if (f.foundingDate) org.foundingDate = f.foundingDate
  if (f.teamSize !== null) org.numberOfEmployees = { '@type': 'QuantitativeValue', value: f.teamSize }
  if (f.priceRange) org.priceRange = f.priceRange
  if (f.parentOrganization) org.parentOrganization = { '@type': 'Organization', ...f.parentOrganization }

  // Credentials come from content/team.ts (owner-confirmed: Niyamul is a
  // GoHighLevel Certified Admin, Jewel is not). url is the person's team page;
  // sameAs lists their public profiles.
  const people = f.leaders.map((l) => {
    const member = teamMemberBySlug(l.id)
    const person: Record<string, unknown> = {
      '@type': 'Person',
      '@id': personId(l.id),
      name: l.name,
      jobTitle: l.jobTitle,
      image: l.image,
      url: `${SITE_URL}/team/${l.id}`,
      worksFor: { '@id': ORG_ID },
      sameAs: [l.linkedin, member?.upwork, member?.facebook].filter(Boolean),
    }
    if (member?.certifications.length) {
      person.hasCredential = member.certifications.map((name) => ({ '@type': 'EducationalOccupationalCredential', name }))
    }
    return person
  })

  const website = {
    '@type': 'WebSite',
    '@id': WEBSITE_ID,
    url: `${SITE_URL}/`,
    name: f.brand.name,
    inLanguage: 'en-US',
    publisher: { '@id': ORG_ID },
    potentialAction: {
      '@type': 'SearchAction',
      target: { '@type': 'EntryPoint', urlTemplate: `${SITE_URL}/blog?q={search_term_string}` },
      'query-input': 'required name=search_term_string',
    },
  }

  return { '@context': 'https://schema.org', '@graph': [org, ...people, website] }
}
